// src/components/charts/ScopeTrendsLineChart.js

import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

// ✅ NEW: choose the correct date for charting
function getChartDate(h) {
  return h?.activity_date || h?.date || h?.uploaded_at || null;
}

function formatDateLabel(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

function dayKeyFromValue(value) {
  if (!value) return "unknown";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function hasRealScopeFields(history) {
  return (history || []).some((h) => {
    return (
      h &&
      (h.scope_1_kgco2e !== undefined ||
        h.scope_2_kgco2e !== undefined ||
        h.scope_3_kgco2e !== undefined)
    );
  });
}

export default function ScopeTrendsLineChart({
  history = [],
  baseSummary = {
    scope_1_kgco2e: 0,
    scope_2_kgco2e: 0,
    scope_3_kgco2e: 0,
    total_kgco2e: 0,
  },
  mode = "cumulative",
}) {
  const data = useMemo(() => {
    const rows = Array.isArray(history) ? history : [];
    if (rows.length === 0) return [];

    // ✅ Sort by chart date asc (NOT uploaded_at)
    const sorted = [...rows].sort((a, b) => {
      const ta = new Date(getChartDate(a) || 0).getTime();
      const tb = new Date(getChartDate(b) || 0).getTime();
      return ta - tb;
    });

    const useReal = hasRealScopeFields(sorted);

    // Proportions fallback
    const baseTotal = safeNumber(baseSummary?.total_kgco2e);
    const p1 = baseTotal > 0 ? safeNumber(baseSummary?.scope_1_kgco2e) / baseTotal : 0;
    const p2 = baseTotal > 0 ? safeNumber(baseSummary?.scope_2_kgco2e) / baseTotal : 0;
    const p3 = baseTotal > 0 ? safeNumber(baseSummary?.scope_3_kgco2e) / baseTotal : 0;

    // 1) Aggregate by day using chart date
    const byDay = new Map();
    for (const h of sorted) {
      const chartDate = getChartDate(h);
      const k = dayKeyFromValue(chartDate);

      if (!byDay.has(k)) {
        byDay.set(k, {
          dayKey: k,
          labelValue: chartDate || k,
          s1: 0,
          s2: 0,
          s3: 0,
        });
      }
      const bucket = byDay.get(k);

      if (useReal) {
        bucket.s1 += safeNumber(h?.scope_1_kgco2e);
        bucket.s2 += safeNumber(h?.scope_2_kgco2e);
        bucket.s3 += safeNumber(h?.scope_3_kgco2e);
      } else {
        const t = safeNumber(h?.total_kgco2e);
        bucket.s1 += t * p1;
        bucket.s2 += t * p2;
        bucket.s3 += t * p3;
      }
    }

    const daily = Array.from(byDay.values()).sort((a, b) => a.dayKey.localeCompare(b.dayKey));

    if (mode === "daily") {
      return daily.map((d) => ({
        date: formatDateLabel(d.labelValue),
        scope1: d.s1,
        scope2: d.s2,
        scope3: d.s3,
        total: d.s1 + d.s2 + d.s3,
      }));
    }

    // cumulative
    let c1 = 0,
      c2 = 0,
      c3 = 0;

    return daily.map((d) => {
      c1 += d.s1;
      c2 += d.s2;
      c3 += d.s3;
      return {
        date: formatDateLabel(d.labelValue),
        scope1: c1,
        scope2: c2,
        scope3: c3,
        total: c1 + c2 + c3,
      };
    });
  }, [history, baseSummary, mode]);

  if (!data.length) {
    return <div style={{ padding: 12, opacity: 0.8 }}>No trend data available yet.</div>;
  }

  return (
    <div style={{ width: "100%", height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickMargin={8} />
          <YAxis
            tickFormatter={(v) => {
              const n = safeNumber(v);
              if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
              if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
              return `${Math.round(n)}`;
            }}
          />
          <Tooltip
            formatter={(value, name) => {
              const n = safeNumber(value);
              const label =
                name === "scope1"
                  ? "Scope 1"
                  : name === "scope2"
                  ? "Scope 2"
                  : name === "scope3"
                  ? "Scope 3"
                  : "Total";
              return [`${n.toLocaleString()} kg CO₂e`, label];
            }}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Legend
            formatter={(value) => {
              if (value === "scope1") return "Scope 1";
              if (value === "scope2") return "Scope 2";
              if (value === "scope3") return "Scope 3";
              if (value === "total") return "Total";
              return value;
            }}
          />

          <Line
  type="monotone"
  dataKey="scope1"
  dot={false}
  strokeWidth={2}
  stroke="#134E4A"   
/>

<Line
  type="monotone"
  dataKey="scope2"
  dot={false}
  strokeWidth={2}
  stroke="#2FAF7A"   
/>

<Line
  type="monotone"
  dataKey="scope3"
  dot={false}
  strokeWidth={2}
  stroke="#96bdab"   
/>

        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
